import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { formatDate } from '../../lib/format';

export interface RetireDialogProps {
  /** What is being retired, shown so a mis-click is obvious before confirming. */
  subject: string;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}

/** Suggestions, not a closed list — the free-text field stays authoritative. */
const SUGGESTIONS = ['retire.reasonReplaced', 'retire.reasonObsolete', 'retire.reasonRemoved'];

/**
 * Confirms a retirement and captures why.
 *
 * The reason is mandatory: a record that simply disappeared from the active list is
 * the thing that makes an audit trail useless six months later, when someone is
 * trying to work out what happened to a boiler.
 */
export function RetireDialog({ subject, onConfirm, onCancel }: RetireDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (reason.trim().length < 3 || busy) return;

    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('retire.failed'));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-3 sm:items-center">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label={t('retire.heading', { subject })}
        className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-950 p-4"
      >
        <p className="flex items-start gap-2 text-sm font-semibold text-slate-100">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-400" aria-hidden="true" />
          {t('retire.heading', { subject })}
        </p>
        <p className="mt-1 text-xs text-slate-400">{t('retire.explain')}</p>

        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('retire.reasonPlaceholder')}
          aria-label={t('retire.reason')}
          maxLength={500}
          autoFocus
          className="mt-3 w-full rounded border border-slate-700 bg-slate-900 px-3 py-3 text-sm"
        />

        <div className="mt-2 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setReason(t(key))}
              className="min-h-[36px] rounded-full border border-slate-700 bg-transparent px-3 text-xs text-slate-300"
            >
              {t(key)}
            </button>
          ))}
        </div>

        {error && (
          <p role="alert" className="mt-2 text-sm text-red-400">
            {error}
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={reason.trim().length < 3 || busy}
            className="min-h-[48px] flex-1 rounded-lg bg-red-700 text-sm font-semibold text-white disabled:opacity-40"
          >
            {busy ? t('retire.retiring') : t('retire.confirm')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[48px] rounded-lg bg-transparent px-4 text-sm text-slate-400"
          >
            {t('retire.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Greyed marker with the when and why behind a tooltip. */
export function RetiredBadge({
  retiredAt,
  retiredReason,
}: {
  retiredAt: string | null;
  retiredReason: string | null;
}) {
  const { t } = useTranslation();

  const detail = [formatDate(retiredAt), retiredReason].filter(Boolean).join(' — ');

  return (
    <span
      title={detail || undefined}
      className="shrink-0 rounded border border-slate-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
    >
      {t('retire.badge')}
    </span>
  );
}
